import { ID_PARAMS, LOCALES, RMH_PATIENT_PATHS } from "@rmh/defs";

// Long enough for any real route plus its query, short enough that a caller
// cannot stuff a payload into an email we send.
const MAX_DESTINATION_LENGTH = 512;

const idParamRegex = (param: string) =>
  param === ":dayIndex" ? "[0-9]+" : "[A-Za-z0-9._@:-]+";

/**
 * Turns a route constant containing `:id`-style params into an anchored regex
 * that matches a concrete pathname. The param character class deliberately
 * excludes `/`, so a param can never swallow extra path segments.
 *
 * Also used by the patient middleware to match a request against its list of
 * permitted routes.
 */
export const replaceIdParamsInPathWithRegex = (path: string) => {
  let modifiedPath = path;
  ID_PARAMS.forEach((param) => {
    modifiedPath = modifiedPath.replace(param, idParamRegex(param));
  });
  return new RegExp(`^${modifiedPath}$`);
};

const ALLOWED_ROUTES = Object.values(RMH_PATIENT_PATHS).map(
  replaceIdParamsInPathWithRegex
);

const LOCALE_SEGMENTS = new Set<string>(LOCALES);

/**
 * Drops a single leading locale segment. Only one, so `/es/es/dashboard` stays
 * unmatched rather than being normalised into something the route table
 * accepts.
 */
const stripLocale = (path: string): string => {
  const [, first, ...rest] = path.split("/");

  if (!first || !LOCALE_SEGMENTS.has(first)) {
    return path;
  }

  return rest.length > 0 ? `/${rest.join("/")}` : "/";
};

/**
 * Validates a post-sign-in destination supplied by a caller.
 *
 * This is an allowlist, not a sanitiser: the path has to match one of the
 * patient app's own routes to survive, so there is no "looks relative enough"
 * path through it. That matters because `requestMagicLink` is unauthenticated
 * and emails an arbitrary address - a caller-supplied destination that only
 * had to be relative would turn our own email into a redirect an attacker
 * chose. Matching the route table caps the worst case at "a different page of
 * the patient app".
 *
 * A leading locale segment is stripped before matching and dropped from the
 * result, since the route table is locale-free. Callers navigate with a
 * locale-aware router, which puts the patient's current locale back on.
 *
 * @returns the locale-free destination with its query string, or null when it
 * isn't safe.
 */
export const getValidPatientDestination = (
  candidate: string | null | undefined
): string | null => {
  if (!candidate || candidate.length > MAX_DESTINATION_LENGTH) {
    return null;
  }

  // Control characters can split a header or break out of the surrounding
  // context wherever this value is later interpolated.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(candidate)) {
    return null;
  }

  // Browsers normalise `\` to `/`, so `/\evil.com` is a protocol-relative URL
  // wearing a leading slash and would pass the `//` check below.
  if (candidate.includes("\\")) {
    return null;
  }

  if (!candidate.startsWith("/") || candidate.startsWith("//")) {
    return null;
  }

  const withoutFragment = candidate.split("#")[0] ?? "";
  const queryIndex = withoutFragment.indexOf("?");
  const path =
    queryIndex === -1 ? withoutFragment : withoutFragment.slice(0, queryIndex);
  const query = queryIndex === -1 ? "" : withoutFragment.slice(queryIndex);

  // Percent-encoding has no legitimate use in these routes (every param is an
  // id), and allowing it would let `%2e%2e` or `%2f` reconstitute a traversal
  // after this check but before the browser resolves the URL.
  if (path.includes("%")) {
    return null;
  }

  // `.` and `..` match the id-param character class, so the route table alone
  // would accept `/dashboard/conditions/../careplan`.
  const segments = path.split("/");
  if (segments.some((segment) => segment === "." || segment === "..")) {
    return null;
  }

  const localeFreePath = stripLocale(path);

  if (!ALLOWED_ROUTES.some((route) => route.test(localeFreePath))) {
    return null;
  }

  return `${localeFreePath}${query}`;
};
